import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Item } from "@/lib/types";

type FocusPhase = "focus" | "break" | "long_break" | "idle";
type TimerState = "running" | "paused" | "idle";

const FOCUS_DURATION = 25 * 60;
const BREAK_DURATION = 5 * 60;
const LONG_BREAK_DURATION = 15 * 60;
const CYCLES_BEFORE_LONG_BREAK = 4;

export function useFocusMode() {
  const { user } = useAuth();
  const [item, setItem] = useState<Item | null>(null);
  const [phase, setPhase] = useState<FocusPhase>("idle");
  const [timerState, setTimerState] = useState<TimerState>("idle");
  const [secondsLeft, setSecondsLeft] = useState(FOCUS_DURATION);
  const [cycleCount, setCycleCount] = useState(0);
  const [totalFocusSeconds, setTotalFocusSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (timerState !== "running") return;

    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearTimer();
          return 0;
        }
        if (phase === "focus") {
          setTotalFocusSeconds((t) => t + 1);
        }
        return prev - 1;
      });
    }, 1000);

    return clearTimer;
  }, [timerState, phase, clearTimer]);

  useEffect(() => {
    if (secondsLeft !== 0 || timerState !== "running") return;

    if (phase === "focus") {
      const newCycles = cycleCount + 1;
      setCycleCount(newCycles);
      if (newCycles % CYCLES_BEFORE_LONG_BREAK === 0) {
        setPhase("long_break");
        setSecondsLeft(LONG_BREAK_DURATION);
      } else {
        setPhase("break");
        setSecondsLeft(BREAK_DURATION);
      }
    } else {
      setPhase("focus");
      setSecondsLeft(FOCUS_DURATION);
    }
  }, [secondsLeft, timerState, phase, cycleCount]);

  const startFocus = useCallback((focusItem: Item) => {
    setItem(focusItem);
    setPhase("focus");
    setTimerState("running");
    setSecondsLeft(FOCUS_DURATION);
    setCycleCount(0);
    setTotalFocusSeconds(0);
  }, []);

  const pause = useCallback(() => {
    clearTimer();
    setTimerState("paused");
  }, [clearTimer]);

  const resume = useCallback(() => {
    setTimerState("running");
  }, []);

  const abort = useCallback(async () => {
    clearTimer();
    if (user && totalFocusSeconds > 0) {
      const minutes = Math.floor(totalFocusSeconds / 60);
      if (minutes > 0) {
        const { data } = await supabase
          .from("user_stats")
          .select("total_focus_minutes")
          .eq("user_id", user.id)
          .single();
        if (data) {
          await supabase
            .from("user_stats")
            .update({ total_focus_minutes: data.total_focus_minutes + minutes })
            .eq("user_id", user.id);
        }
      }
    }
    setItem(null);
    setPhase("idle");
    setTimerState("idle");
    setSecondsLeft(FOCUS_DURATION);
    setCycleCount(0);
    setTotalFocusSeconds(0);
  }, [clearTimer, user, totalFocusSeconds]);

  return {
    item,
    phase,
    timerState,
    secondsLeft,
    cycleCount,
    totalFocusSeconds,
    isActive: phase !== "idle",
    startFocus,
    pause,
    resume,
    abort,
  };
}
